import { useEffect, useState } from 'react';
import useSWR from 'swr';
import routes from '@config/routes';
import { PACKAGE_STORAGE } from '@config/storageKeys';
import useStorage from '@hooks/useStorage';
import { serverFetcher } from '@instance';
import type { PropertyDetails } from '@interfaces/expedia/property';
import { useBuildPackagesStore } from '@store/build-package-store';
import { asRecord, readHotelStorage } from '../../SearchHotel/room-prefill';

export const HOTEL_DETAILS_TTL = 60_000;

export function useHotelDetails(id: string | number | null) {
  const hotelId = Number(id);
  const storage = useStorage();
  const hydrated = useBuildPackagesStore((state) => state._hasHydrated);
  const startDate = useBuildPackagesStore((state) => state.hotelStartDate);
  const endDate = useBuildPackagesStore((state) => state.hotelEndDate);
  const roomConfigurations = useBuildPackagesStore(
    (state) => state.roomConfigurations
  );
  const isBundle = Boolean(
    asRecord(readHotelStorage(storage, PACKAGE_STORAGE).selected).tickets
  );
  const canFetch = Boolean(
    hydrated &&
    Number.isSafeInteger(hotelId) &&
    hotelId > 0 &&
    startDate &&
    endDate &&
    roomConfigurations.length
  );
  const result = useSWR<{ propertyDetails: PropertyDetails }>(
    canFetch
      ? [
          routes.propertyDetails,
          hotelId,
          startDate,
          endDate,
          roomConfigurations,
          isBundle,
        ]
      : null,
    async ([
      url,
      propertyId,
      startDate,
      endDate,
      roomConfigurations,
      isBundle,
    ]) => {
      const response = await serverFetcher(url, {
        body: {
          id: propertyId,
          startDate,
          endDate,
          roomConfigurations,
          isBundle,
        },
      });
      if (!response?.propertyDetails)
        throw new Error('Hotel details are unavailable. Please try again.');
      return response;
    },
    {
      dedupingInterval: HOTEL_DETAILS_TTL,
      refreshInterval: HOTEL_DETAILS_TTL,
      revalidateIfStale: true,
      revalidateOnFocus: true,
      errorRetryCount: 1,
    }
  );
  return { ...result, canFetch };
}

// Mount only the hotel the user pauses on, not a request for every visible card.
export function HotelDetailsPrefetch({ hotelId }: { hotelId: number | null }) {
  const [id, setId] = useState<number | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => setId(hotelId), 180);
    const expire = setTimeout(() => setId(null), 10_000);
    return () => {
      clearTimeout(timer);
      clearTimeout(expire);
    };
  }, [hotelId]);
  useHotelDetails(id);
  return null;
}
